import { createSelector } from '@reduxjs/toolkit';
import { CommonRootState } from '@common/types/common-root-state-type';
import { selectConnectedEnginesState } from '@common/reducers/ConnectedEnginesSlice';
import { selectIsDarkMode, selectCurrentUserSession } from '@common-reducers/UserSelectors';

interface GroupedEngine {
  groupKey: string;
  displayName: string;
  type: 'browser' | 'gateway' | 'whatsapp';
  domain: string;
  count: number;
  latestConnectionTime: string;
  engines: any[];
}

/** The display group of an engine in the header list: an online engine by its
 *  engineKind, the server session, or this page's own extension context. */
const engineGroupOf = (engine: any): { groupKey: string; type: GroupedEngine['type']; name: string; domain: string } => {
  if ('engineKind' in engine) {
    return engine.engineKind === 'extension'
      ? { groupKey: 'whatsapp-web', type: 'browser', name: 'WhatsApp Web', domain: 'web.whatsapp.com' }
      : { groupKey: 'whatsapp-gateway', type: 'gateway', name: 'WhatsApp Gateway', domain: 'gateway' };
  }
  if ('type' in engine && engine.type === 'whatsapp') {
    return { groupKey: 'whatsapp-gateway', type: 'whatsapp', name: engine.name, domain: engine.domain };
  }
  // This page's own extension context (ConnectedEnginesSlice `current-extension`).
  return { groupKey: 'current-extension', type: 'browser', name: engine.browserType, domain: engine.domain };
};

const groupEngines = (engines: any[]): GroupedEngine[] => {
  const groupedEngines = engines.reduce((groups: Record<string, { engines: any[], info: any, count: number }>, engine) => {
    const engineInfo = engineGroupOf(engine);
    const groupKey = engineInfo.groupKey;

    if (!groups[groupKey]) {
      groups[groupKey] = {
        engines: [],
        info: engineInfo,
        count: 0
      };
    }
    
    groups[groupKey].engines.push(engine);
    groups[groupKey].count++;
    
    return groups;
  }, {});

  // Convert to array format with proper typing
  return Object.entries(groupedEngines).map(([groupKey, group]) => {
    const { info, count, engines: groupEngines } = group as { engines: any[], info: any, count: number };
    const latestEngine = groupEngines.sort((a, b) => 
      new Date(b.connectionTime).getTime() - new Date(a.connectionTime).getTime()
    )[0];
    
    const displayName = groupKey === 'whatsapp-gateway' ? 'WhatsApp Gateway' : info.name;

    return {
      groupKey,
      displayName,
      type: info.type,
      domain: info.domain,
      count,
      latestConnectionTime: latestEngine.connectionTime,
      engines: groupEngines
    };
  });
};

export const selectEngineState = createSelector(
  [selectConnectedEnginesState, selectCurrentUserSession],
  (engineState, session) => ({
    engines: engineState.engines,
    groupedEngines: groupEngines(engineState.engines),
    status: engineState.status,
    hasGateway: engineState.hasGateway,
    isGatewayConnected: engineState.isGatewayConnected,
    isNotifierConnected: engineState.isNotifierConnected,
    session: session,
    isInQRMode: session?.status === 'qrcode',
  })
);
